/**
 * API client — axios-like thin wrapper using fetch.
 * Access JWT in memory; refresh cookie sent with credentials.
 * Expired access tokens are refreshed once and the request is retried.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4100/api/v1';

const REQUEST_TIMEOUT_MS = 20000;
const REFRESH_LEAD_MS = 60 * 1000;

const SKIP_REFRESH_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/google',
  '/auth/gate-proceed',
  '/auth/resend-verification',
  '/auth/verify-email',
];

let accessTokenMemory = null;
let tokenRefreshHandler = null;
let refreshInFlight = null;
let proactiveTimer = null;

export function setTokenRefreshHandler(fn) {
  tokenRefreshHandler = typeof fn === 'function' ? fn : null;
}

export function setAccessToken(token) {
  accessTokenMemory = token || null;
  scheduleProactiveRefresh(token);
}

export function clearAccessToken() {
  accessTokenMemory = null;
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
}

export function getAccessToken() {
  return accessTokenMemory;
}

function skipRefreshForPath(path) {
  return SKIP_REFRESH_PATHS.some((p) => String(path || '').startsWith(p));
}

function isExpiredAccessError(status, payload) {
  if (status !== 401) return false;
  const code = String((payload && payload.errorCode) || '');
  const message = String((payload && payload.message) || '').toLowerCase();
  return (
    code === 'ERR_UNAUTHORIZED' ||
    message.includes('jwt expired') ||
    message.includes('expired access token') ||
    message.includes('invalid or expired')
  );
}

function readTokenExpiryMs(token) {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return 0;
    const json = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = json + '='.repeat((4 - (json.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return payload.exp ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function scheduleProactiveRefresh(token) {
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  if (!token || !tokenRefreshHandler) return;
  const expMs = readTokenExpiryMs(token);
  if (!expMs) return;
  const delay = expMs - Date.now() - REFRESH_LEAD_MS;
  if (delay <= 0) {
    refreshAccessToken().catch(() => {});
    return;
  }
  proactiveTimer = setTimeout(() => {
    refreshAccessToken().catch(() => {});
  }, delay);
}

async function refreshAccessToken() {
  if (!tokenRefreshHandler) return false;
  if (!refreshInFlight) {
    refreshInFlight = Promise.resolve()
      .then(() => tokenRefreshHandler())
      .finally(() => {
        refreshInFlight = null;
      });
  }
  try {
    await refreshInFlight;
    return true;
  } catch {
    return false;
  }
}

async function request(method, path, body, options = {}) {
  const headers = {
    Accept: 'application/json',
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers || {}),
  };
  if (accessTokenMemory) {
    headers.Authorization = `Bearer ${accessTokenMemory}`;
  }

  const controller = new AbortController();
  const timeoutMs = options.timeoutMs || REQUEST_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (e) {
    const err = new Error(
      e && e.name === 'AbortError'
        ? 'The server took too long to respond. Please try again.'
        : 'Could not reach the server. Check your connection and try again.'
    );
    err.status = 0;
    err.errorCode = e && e.name === 'AbortError' ? 'ERR_TIMEOUT' : 'ERR_NETWORK';
    throw err;
  } finally {
    clearTimeout(timer);
  }

  if (options.responseType === 'blob' && res.ok) {
    return { data: await res.blob(), status: res.status };
  }

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (
    !res.ok &&
    !options.skipAuthRefresh &&
    !options._retry &&
    !skipRefreshForPath(path) &&
    isExpiredAccessError(res.status, payload)
  ) {
    const refreshed = await refreshAccessToken();
    if (refreshed && accessTokenMemory) {
      return request(method, path, body, { ...options, _retry: true });
    }
  }

  if (!res.ok) {
    const err = new Error((payload && payload.message) || `Request failed (${res.status})`);
    err.status = res.status;
    err.errorCode = payload && payload.errorCode;
    err.data = payload;
    throw err;
  }

  return { data: payload, status: res.status };
}

const apiClient = {
  get: (path, options) => request('GET', path, undefined, options),
  post: (path, body, options) => request('POST', path, body || {}, options),
  patch: (path, body, options) => request('PATCH', path, body || {}, options),
};

export default apiClient;
